import {
  action,
  computed,
  makeObservable,
  onBecomeObserved,
  reaction,
} from "mobx"

import { MassPayoutsCollectionFilter } from "@src/pages/massPayouts/crypto/classess/MassPayoutsCryptoCollection"
import { cardMassPayoutsApi, catchErrors } from "@src/shared/api"
import {
  CardMassPayoutPayoutResponse,
  CardMassPayoutPayoutResponseStatusEnum,
  Pagination,
} from "@src/shared/api/generated"
import { CollectionWithPagination } from "@src/shared/classes/Collection/CollectionWithPagination"
import { loading } from "@src/shared/classes/utils/loading"
import { CollectionFilters } from "@src/shared/componemts/TableFilter/classes/CollectionFilters"
import { filtersPresets } from "@src/shared/componemts/TableFilter/classes/constants"

export type PayoutsCollectionLoadRequest = {
  date?: {
    dateFrom?: string
    dateTo?: string
  }
  externalId?: string
  id: string
  order?: "asc" | "desc"
  page?: number
  status?: "completed" | "failed" | "new" | "pending"
}

export enum CardMassPayoutStatus {
  Failed = "failed",
  New = "new",
  Pending = "pending",
  Processed = "processed",
}

export type CardMassPayoutType = CardMassPayoutPayoutResponse & {
  error?: {
    code: number
    message: string
  }
}

export class PayoutsCardCollection extends loading(
  ["fetch"],
  CollectionWithPagination<CardMassPayoutType>,
) {
  constructor(id: string) {
    super()

    makeObservable(this, {
      fetch: action.bound,
      filter: computed,
      forDownload: action.bound,
      id: computed,
    })

    onBecomeObserved(this, "isLoading", () => this.load({ id }))

    reaction(
      () => this.filter.state,
      (v) => v && this.load(v),
    )
  }

  async fetch(req: PayoutsCollectionLoadRequest) {
    try {
      if (!req.id) {
        req.id = this.id
      }

      const { data: { data } = {} } = await cardMassPayoutsApi.listCardPayouts(
        req.id,
        req.externalId,
        req.date?.dateFrom,
        req.date?.dateTo,
        req.status,
        req.order,
        this.calcOffset(req.page),
        this.pageSize,
      )

      if (data && data.data) {
        return {
          data: data.data as CardMassPayoutType[],
          pagination: data.pagination as Pagination,
        }
      }
    } catch (error: any) {
      catchErrors(error)
    }

    return null
  }

  forDownload(payouts: CardMassPayoutType[]) {
    return payouts.map((p) => ({
      "id": p.id,
      // eslint-disable-next-line perfectionist/sort-objects
      "Date and Time": p.created_at,
      "Status": p.status,
      // eslint-disable-next-line perfectionist/sort-objects
      "Card": p.data?.card_number,
      "Currency": p.data?.symbol,
      "Network": "fiat",
      // eslint-disable-next-line perfectionist/sort-objects
      "Amount": p.data?.amount,
      // eslint-disable-next-line perfectionist/sort-objects
      "Errors": p.error ? `${p.error.code} ${p.error.message}` : "",
    }))
  }

  get failed() {
    return this.items.filter(
      (i) => i.status === CardMassPayoutPayoutResponseStatusEnum.Failed,
    )
  }

  get filter() {
    return new CollectionFilters<MassPayoutsCollectionFilter>([
      CollectionFilters.createField("externalId").search({
        col: 6,
        label: "Search for External ID",
        placeholder: "External ID",
      }),
      CollectionFilters.createField("date").date({ col: 8, label: "Date" }),
      {
        ...filtersPresets.order,
        col: 4,
        label: "Select on order",
        placeholder: "Select",
      },
    ])
  }

  get id() {
    return (this.state.prevRequest as PayoutsCollectionLoadRequest).id
  }
}
